import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { hikingPath } from '../utils/paths';
import { formatScheduleDateTime, toScheduleInputValue } from '../utils/scheduleTimeZone';
import { OUTING_PACE_GROUPS, DEFAULT_PACK_LIST, formatDistance } from '../config/hikerProfileOptions';
import './HikingOutingScheduling.css';

const toApiDateTime = (localValue) => {
  if (!localValue) return null;
  // datetime-local → yyyy-MM-dd'T'HH:mm:ss
  return localValue.length === 16 ? `${localValue}:00` : localValue;
};

const parsePackList = (text) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const EMPTY_FORM = {
  title: '',
  description: '',
  outingDate: '',
  trailId: '',
  meetingLocation: '',
  meetingNotes: '',
  expectedDurationMinutes: '',
  pace: 'STEADY',
  hikerCapacity: '',
  packList: DEFAULT_PACK_LIST.join('\n'),
};

const HikingOutingScheduling = () => {
  const { id: clubId } = useParams();
  const { user, isAdmin } = useAuth();
  const [club, setClub] = useState(null);
  const [outings, setOutings] = useState([]);
  const [trails, setTrails] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canManage, setCanManage] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const clubData = await api.getClubById(clubId);
      setClub(clubData);
      const isOwner = user?.id && clubData.adminUserId === user.id;
      const manage = !!(isOwner || isAdmin());
      setCanManage(manage);
      const [outingData, trailData] = await Promise.all([
        api.getHikingOutings(clubId),
        api.getHikingTrails(clubId, true),
      ]);
      setOutings(Array.isArray(outingData) ? outingData : []);
      setTrails(Array.isArray(trailData) ? trailData : []);
    } catch (err) {
      setError(err.message || 'Failed to load outings');
    } finally {
      setLoading(false);
    }
  }, [clubId, user?.id, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const startEdit = (outing) => {
    setEditingId(outing.id);
    setForm({
      title: outing.title || '',
      description: outing.description || '',
      outingDate: toScheduleInputValue(outing.outingDate),
      trailId: outing.trailId || '',
      meetingLocation: outing.meetingLocation || '',
      meetingNotes: outing.meetingNotes || '',
      expectedDurationMinutes:
        outing.expectedDurationMinutes != null ? String(outing.expectedDurationMinutes) : '',
      pace: outing.pace || 'STEADY',
      hikerCapacity: outing.hikerCapacity != null ? String(outing.hikerCapacity) : '',
      packList: (outing.packList || []).join('\n'),
    });
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    outingDate: toApiDateTime(form.outingDate),
    trailId: form.trailId || null,
    meetingLocation: form.meetingLocation.trim(),
    meetingNotes: form.meetingNotes.trim() || null,
    expectedDurationMinutes: form.expectedDurationMinutes
      ? Number(form.expectedDurationMinutes)
      : null,
    pace: form.pace || null,
    hikerCapacity: form.hikerCapacity ? Number(form.hikerCapacity) : null,
    packList: parsePackList(form.packList),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload = buildPayload();
      if (editingId) {
        await api.updateHikingOuting(editingId, payload, user.id);
        setSuccess('Outing updated');
      } else {
        await api.createHikingOuting(clubId, payload, user.id);
        setSuccess('Outing published');
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save outing');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (outingId) => {
    if (!window.confirm('Cancel this outing?')) return;
    try {
      await api.cancelHikingOuting(outingId, user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to cancel outing');
    }
  };

  if (loading) return <div className="loading">Loading outings...</div>;

  return (
    <div className="hiking-outing-sched">
      <div className="hiking-outing-sched__head">
        <p className="hiking-outing-sched__eyebrow">
          <Link to={hikingPath(`/clubs/${clubId}`)}>{club?.name || 'Club'}</Link>
        </p>
        <h1>Group outings</h1>
        <p>Set the time, meeting spot, trail, pace, and what everyone should carry.</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {canManage && (
        <form className="hiking-outing-sched__form" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Edit outing' : 'Publish an outing'}</h2>
          <label>
            Title
            <input name="title" value={form.title} onChange={onChange} required maxLength={100} />
          </label>
          <label>
            Date &amp; time
            <input
              name="outingDate"
              type="datetime-local"
              value={form.outingDate}
              onChange={onChange}
              required
            />
          </label>
          <label>
            Meeting location
            <input
              name="meetingLocation"
              value={form.meetingLocation}
              onChange={onChange}
              required
              placeholder="Trailhead kiosk, parking lot, carpool spot..."
            />
          </label>
          <label>
            Meeting notes
            <textarea
              name="meetingNotes"
              value={form.meetingNotes}
              onChange={onChange}
              rows={2}
              placeholder="Arrive 10 min early. Cell service drops past the kiosk."
            />
          </label>
          <label>
            Trail
            <select name="trailId" value={form.trailId} onChange={onChange}>
              <option value="">Select a published trail</option>
              {trails.map((trail) => (
                <option key={trail.id} value={trail.id}>
                  {trail.title}
                  {trail.distanceKm != null ? ` (${formatDistance(trail.distanceKm)})` : ''}
                </option>
              ))}
            </select>
          </label>
          {trails.length === 0 && (
            <p className="hiking-outing-sched__hint">
              <Link to={hikingPath(`/clubs/${clubId}/trails`)}>Publish a trail</Link> first so hikers
              can see distance and climb.
            </p>
          )}
          <div className="hiking-outing-sched__row">
            <label>
              Pace
              <select name="pace" value={form.pace} onChange={onChange}>
                {OUTING_PACE_GROUPS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Time on trail (min)
              <input
                name="expectedDurationMinutes"
                type="number"
                min="15"
                max="1440"
                step="15"
                value={form.expectedDurationMinutes}
                onChange={onChange}
                placeholder="Defaults to the trail estimate"
              />
            </label>
            <label>
              Hiker capacity (optional)
              <input
                name="hikerCapacity"
                type="number"
                min="1"
                value={form.hikerCapacity}
                onChange={onChange}
              />
            </label>
          </div>
          <label>
            Pack list (one item per line)
            <textarea name="packList" value={form.packList} onChange={onChange} rows={5} />
          </label>
          <label>
            Description
            <textarea name="description" value={form.description} onChange={onChange} rows={3} />
          </label>
          <div className="hiking-outing-sched__form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save changes' : 'Publish outing'}
            </button>
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>
      )}

      <div className="hiking-outing-sched__list">
        {outings.length === 0 && <p>No outings yet.</p>}
        {outings.map((outing) => (
          <article key={outing.id} className="hiking-outing-sched__card">
            <div>
              <h3>
                {outing.title}
                {outing.status === 'CANCELLED' && (
                  <span className="hiking-outing-sched__badge">Cancelled</span>
                )}
                {outing.status === 'COMPLETED' && (
                  <span className="hiking-outing-sched__badge">Completed</span>
                )}
              </h3>
              <p>{formatScheduleDateTime(outing.outingDate)}</p>
              <p>Meet: {outing.meetingLocation}</p>
              {outing.trail?.title && <p>Trail: {outing.trail.title}</p>}
            </div>
            <div className="hiking-outing-sched__actions">
              <Link to={hikingPath(`/outings/${outing.id}`)} className="btn btn-primary">
                Open
              </Link>
              {canManage && outing.status === 'SCHEDULED' && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => startEdit(outing)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleCancel(outing.id)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default HikingOutingScheduling;
